import React, { useEffect, useState, useRef } from "react";
import { Alert, message, Spin } from "antd";
import MainWrapper from "../../components/MainWrapper";
import SpinerIndicator from "../../components/SpinerIndicator";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { transactionApi } from "../../config/axiosConfig";
import SuccessMessage from "../../components/SuccessMessage";
import styled from "styled-components";
import PapayaLogo from "../../assets/papayapay_new_logo.png";

const PageContainer = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  overflow: hidden;

  /* Animated Geometric Background */
  &::before, &::after {
    content: "";
    position: absolute;
    width: 600px;
    height: 600px;
    border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%;
    filter: blur(80px);
    z-index: 0;
    opacity: 0.25;
    animation: morph 20s linear infinite;
  }

  &::before {
    background: #e9bc41ff; /* Light yellow */
    top: -10%;
    left: -10%;
  }

  &::after {
    background: #b87604ff; /* Dark yellow */
    bottom: -10%;
    right: -10%;
    animation-delay: -10s;
  }

  @keyframes morph {
    0% { border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; transform: translate(0, 0) rotate(0deg); }
    33% { border-radius: 70% 30% 50% 50% / 30% 30% 70% 70%; transform: translate(100px, 100px) rotate(120deg); }
    66% { border-radius: 100% 60% 60% 100% / 100% 100% 60% 60%; transform: translate(-50px, 150px) rotate(240deg); }
    100% { border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; transform: translate(0, 0) rotate(360deg); }
  }
`;

const GeometricShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;

  .shape {
    position: absolute;
    opacity: 0.35; /* Significantly increased visibility */
    filter: drop-shadow(0 0 15px rgba(0,0,0,0.1));
  }

  .circle { border-radius: 50%; }
  .square { border-radius: 4px; }
  .triangle {
    width: 0;
    height: 0;
    border-left: 25px solid transparent;
    border-right: 25px solid transparent;
    border-bottom: 50px solid;
    background-color: transparent !important;
  }

  /* Different animations for variety */
  .float-slow { animation: float 35s infinite linear; }
  .float-fast { animation: float 15s infinite linear; }
  .float-reverse { animation: float-rev 25s infinite linear; }
  .pulse { animation: pulse 10s infinite ease-in-out; }

  @keyframes float {
    from { transform: translate(0, 0) rotate(0deg); }
    to { transform: translate(400px, -400px) rotate(360deg); }
  }

  @keyframes float-rev {
    from { transform: translate(0, 0) rotate(360deg); }
    to { transform: translate(-300px, 300px) rotate(0deg); }
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.3; }
    50% { transform: scale(1.2); opacity: 0.5; }
  }
`;

const ContentCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  width: 100%;
  max-width: 900px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  position: relative;
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const LogoHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 30px;
  background-color: #212121;
  padding: 4px;
  border-radius: 10px;
  width: fit-content;
  margin-left: auto;
  img {
    height: 50px;
  }
`;

const RemindersBox = styled.div`
  background-color: #f9d959ff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 40px;
  h4 {
    font-weight: bold;
    margin-bottom: 12px;
    font-size: 16px;
  }
  ul {
    margin: 0;
    padding-left: 20px;
    li {
      margin-bottom: 4px;
      font-size: 14px;
    }
  }
`;

const PaymentBody = styled.div`
  display: flex;
  gap: 40px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const QRColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const QRBox = styled.div`
  border: 1px solid #eee;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  width: 100%;
  max-width: 280px;
  img {
    width: 100%;
  }
`;

const QRMeta = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 20px;
  p {
    margin: 2px 0;
  }
`;

const AmountDisplay = styled.div`
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 15px;
`;

const TimerDisplay = styled.div`
  background-color: #fff1b8;
  padding: 5px 15px;
  border-radius: 20px;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
`;

const InfoColumn = styled.div`
  flex: 1.5;
  width: 100%;
`;

const FieldWrapper = styled.div`
  margin-bottom: 16px;
  label {
    display: block;
    font-size: 13px;
    color: #333;
    margin-bottom: 6px;
    font-weight: 500;
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ValueBox = styled.div`
  flex: 1;
  background-color: #f5f5f5;
  padding: 10px 15px;
  border-radius: 4px;
  font-size: 15px;
  font-weight: 500;
  min-height: 44px;
  display: flex;
  align-items: center;
`;

const CopyButton = styled.button`
  background-color: #ffd666;
  border: none;
  border-radius: 4px;
  padding: 0 15px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background-color: #ffc53d;
  }
`;

const InfoBox = styled.div`
  border: 1px solid #ffd666;
  border-radius: 4px;
  padding: 12px;
  margin: 20px 0;
  font-size: 12px;
  color: #666;
  line-height: 1.5;
`;

const WarningList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  li {
    font-size: 12px;
    color: #cf1322;
    margin-bottom: 6px;
    position: relative;
    padding-left: 12px;
    &::before {
      content: "•";
      position: absolute;
      left: 0;
    }
  }
`;

const FooterText = styled.div`
  text-align: center;
  margin-top: 40px;
  font-size: 12px;
  color: #666;
  line-height: 1.6;
  a {
    color: inherit;
    text-decoration: underline;
    font-weight: 500;
  }
`;

const VNDEFGQrCodeInfo = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading, error, data: verifyTranData } = useSelector(
    (state) => state.getVerifyManualTransactionReducer
  );

  const postQrInitManualState = useSelector(
    (state) => state.postQrInitManualReducer
  );

  const {
    data: qrInitData,
    error: qrInitError,
    isLoading: qrInitLoading,
  } = postQrInitManualState;

  const bankName = qrInitData?.bankName || qrInitData?.subBankName || "N/A";
  const accountNo = qrInitData?.accountNo || qrInitData?.cardNo || "N/A";
  const accountName = qrInitData?.accountName || qrInitData?.cardName || "N/A";
  const referenceNo = qrInitData?.customAdditional || "N/A";

  const qrCodeText = qrInitData?.qrCode || qrInitData?.qrUrl;
  const qrImage = qrCodeText?.startsWith("http")
    ? qrCodeText
    : qrCodeText
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
        qrCodeText
      )}`
    : null;


  /* ⏱️ QR Expiry Timer (10 min) */
  const [timeLeft, setTimeLeft] = useState(600);

  /* 🔁 Polling ref */
  const pollingTimeoutRef = useRef(null);

  /* ❌ QR Status Error */
  const [qrStatusError, setQrStatusError] = useState("");

  /* 💳 Payment Status */
  const [paymentStatus, setPaymentStatus] = useState("PENDING");

  /* -------------------- QR TIMER -------------------- */
  useEffect(() => {
    if (!qrInitData) return;

    setTimeLeft(600);

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [qrInitData]);

  const formatTime = (seconds) => {
    const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
    const ss = String(seconds % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  /* -------------------- QR STATUS POLLING -------------------- */
  useEffect(() => {
    if (!verifyTranData?.trxRef || !verifyTranData?.merchantId) return;

    let isCancelled = false;

    const checkQrStatus = async () => {
      try {
        const res = await transactionApi.get(
          `vnd/transactionvnd/qr-vnd-check/${verifyTranData.trxRef}/${verifyTranData.merchantId}`
        );

        const status = res?.data?.data?.status;

        if (status === "00_Initiated" || status === "01_Pending") {
          if (!isCancelled) {
            pollingTimeoutRef.current = setTimeout(checkQrStatus, 5000);
          }
          return;
        }

        if (status === "02_Paid") {
          setPaymentStatus("PAID");
          return;
        }

        if (status === "03_Rejected") {
          setPaymentStatus("REJECTED");
          setQrStatusError("Payment was rejected.");
        }
      } catch (err) {
        // Silent error for polling
      }
    };

    checkQrStatus();

    return () => {
      isCancelled = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [verifyTranData]);

  useEffect(() => {
    if (!verifyTranData) {
      navigate(`/vnd/${params.id}`);
    }
  }, []);

  useEffect(() => {
    if (paymentStatus !== "PAID") return;

    const timer = setTimeout(() => {
      const getRedirectionUrl = localStorage.getItem("redirectionUrl");
      if (getRedirectionUrl) {
        window.location.replace(getRedirectionUrl);
        localStorage.removeItem("redirectionUrl");
      } else if (verifyTranData?.redirectUrl) {
        window.location.replace(verifyTranData.redirectUrl);
      } else if (verifyTranData?.siteUrl) {
        window.location.replace(verifyTranData.siteUrl);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [paymentStatus, verifyTranData]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard");
  };

  const isQrInvalid = timeLeft === 0 || paymentStatus === "REJECTED";

  if (isLoading || qrInitLoading) return <SpinerIndicator />;

  if (paymentStatus === "PAID") {
    return (
      <PageContainer>
        <GeometricShapes>
          <div className="shape circle float-slow" style={{ width: 100, height: 100, top: '20%', left: '10%', backgroundColor: '#fadb14' }} />
          <div className="shape square float-reverse" style={{ width: 150, height: 150, top: '60%', left: '80%', backgroundColor: '#faad14' }} />
          <div className="shape triangle pulse" style={{ borderBottomColor: '#ffd666', top: '15%', left: '70%' }} />
          <div className="shape circle float-fast" style={{ width: 50, height: 50, top: '80%', left: '20%', backgroundColor: '#000' }} />
        </GeometricShapes>
        <ContentCard style={{ textAlign: "center" }}>
          <SuccessMessage width={"100%"} height={"200px"} />
          <h2 style={{ color: "#52c41a", marginTop: 20 }}>Payment Successful</h2>
          <p>Your payment has been received successfully.</p>
          <p>Redirecting you back in 5 seconds...</p>
        </ContentCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <GeometricShapes>
        <div className="shape circle float-slow" style={{ width: 120, height: 120, top: '15%', left: '5%', backgroundColor: '#fadb14' }} />
        <div className="shape square float-reverse" style={{ width: 180, height: 180, top: '65%', left: '85%', backgroundColor: '#faad14' }} />
        <div className="shape triangle float-fast" style={{ borderBottomColor: '#ffd666', top: '10%', left: '75%' }} />
        <div className="shape circle pulse" style={{ width: 60, height: 60, top: '85%', left: '15%', backgroundColor: '#000' }} />
        <div className="shape square float-slow" style={{ width: 140, height: 140, top: '35%', left: '45%', backgroundColor: '#fff', animationDelay: '-8s' }} />
        <div className="shape circle float-reverse" style={{ width: 100, height: 100, top: '50%', left: '5%', backgroundColor: '#e9bc41' }} />
        <div className="shape triangle pulse" style={{ borderBottomColor: '#fa8c16', top: '75%', left: '60%', animationDelay: '-3s' }} />
        <div className="shape circle float-fast" style={{ width: 40, height: 40, top: '25%', left: '90%', backgroundColor: '#fadb14' }} />
      </GeometricShapes>
      <ContentCard>
        <LogoHeader>
          <img src={PapayaLogo} alt="Papaya Pay" />
        </LogoHeader>

        <RemindersBox>
          <h4>NHỮNG NHẮC NHỞ</h4>
          <ul>
            <li>Chấp nhận chuyển khoản liên ngân hàng.</li>
            <li>Quá trình phê duyệt sẽ hoàn tất trong vòng 2 phút sau khi thanh toán.</li>
          </ul>
        </RemindersBox>

        <PaymentBody>
          <QRColumn>
            <QRBox>
              {isQrInvalid ? (
                <div style={{ padding: 40, color: "#cf1322" }}>
                  QR Code Expired
                </div>
              ) : (
                <img src={qrImage} alt="Payment QR" />
              )}
            </QRBox>
            <QRMeta>
              <p style={{ color: "#cf1322" }}>Mã QR chỉ dùng được một lần.</p>
              <p>REF: {referenceNo}</p>
            </QRMeta>
            <AmountDisplay>
              {new Intl.NumberFormat("en-US").format(verifyTranData?.amount ?? 0)} ₫
            </AmountDisplay>
            <TimerDisplay>
              <span role="img" aria-label="clock">🕒</span> {formatTime(timeLeft)}
            </TimerDisplay>
          </QRColumn>

          <InfoColumn>
            <FieldWrapper>
              <label>Tên Ngân Hàng</label>
              <ValueBox>{bankName}</ValueBox>
            </FieldWrapper>

            <FieldWrapper>
              <label>Tài Khoản / Số Di Động</label>
              <InputGroup>
                <ValueBox>{accountNo}</ValueBox>
                <CopyButton onClick={() => handleCopy(accountNo)}>Sao Chép</CopyButton>
              </InputGroup>
            </FieldWrapper>

            <FieldWrapper>
              <label>Tên người nhận</label>
              <InputGroup>
                <ValueBox>{accountName}</ValueBox>
                <CopyButton onClick={() => handleCopy(accountName)}>Sao Chép</CopyButton>
              </InputGroup>
            </FieldWrapper>

            <FieldWrapper>
              <label>Số tiền chuyển</label>
              <InputGroup>
                <ValueBox>{new Intl.NumberFormat("en-US").format(verifyTranData?.amount ?? 0)}</ValueBox>
                <CopyButton onClick={() => handleCopy(verifyTranData?.amount)}>Sao Chép</CopyButton>
              </InputGroup>
            </FieldWrapper>

            <FieldWrapper>
              <label>Nội Dung Chuyển Khoản</label>
              <InputGroup>
                <ValueBox>{referenceNo}</ValueBox>
                <CopyButton onClick={() => handleCopy(referenceNo)}>Sao Chép</CopyButton>
              </InputGroup>
            </FieldWrapper>

            <InfoBox>
              Vui lòng sử dụng nút sao chép để sao chép toàn bộ nội dung lời nhắn, sau đó dán vào ô lời nhắnบน màn hình chuyển khoản.
            </InfoBox>

            <WarningList>
              <li>Vui lòng điền chính xác nội dung trên vào phần tin nhắn.</li>
              <li>Vui lòng kiểm tra kỹ số tài khoản của người nhận trước khi chuyển khoản.</li>
              <li>Chúng tôi sẽ không chịu trách nhiệm cho bất kỳ tổn thất nào do nhập sai số tài khoản hoặc chuyển khoản đến tài khoản đã bị đóng.</li>
            </WarningList>
          </InfoColumn>
        </PaymentBody>

        <FooterText>
          <a href={verifyTranData?.redirectUrl || verifyTranData?.siteUrl}>
            Nhấn vào đây nếu bạn không được chuyển hướng về trang của merchant sau khi thanh toán thành công
          </a>
        </FooterText>
      </ContentCard>
    </PageContainer>
  );
};

export default VNDEFGQrCodeInfo;
